import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Wrench, MapPin, BadgeCheck, Star, Users, Clock, Car,
  ClipboardList, Phone, Truck, CheckCircle2, XCircle,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { MaskedLine, Reveal, Eyebrow, SectionHeading } from "../components/Reveal";
import { CTAPair } from "../components/CTAButtons";
import { ServicesStrip } from "../components/ServicesStrip";
import { Marquee } from "../components/Marquee";
import { SocialSection } from "../components/SocialSection";
import { ReviewsSection } from "../components/ReviewsSection";
import { FAQSection } from "../components/FAQSection";
import { GallerySection } from "../components/GallerySection";
import { CTABand } from "../components/CTABand";
import { SITE } from "../config/site";
import { LOCATIONS } from "../data/locations";
import { SOCIAL_ICONS } from "../components/Footer";

const WHY = [
  { icon: Wrench, title: "A Real Professional", text: "Nearly 20 years of hands-on automotive repair experience — the same technician who documents real repairs for millions online." },
  { icon: Car, title: "We Come to You", text: "Your home, your workplace, your driveway. No drop-offs, no shuttles, no waiting rooms." },
  { icon: ShieldCheck, title: "Warranty-Backed", text: "Quality parts and labor, covered in writing. Old parts shown to you before we leave." },
  { icon: BadgeCheck, title: "Transparent Pricing", text: "Exact quotes before we touch a bolt. The price you're quoted is the price you pay." },
  { icon: Star, title: "Real Customer Reviews", text: "Verified five-star brake jobs across the Merrimack Valley — read them on Google." },
  { icon: Users, title: "Nearly 2 Million Followers", text: "A massive online audience built on documented, real-world repair work — not stock photos." },
];

const STEPS = [
  { icon: ClipboardList, title: "Tell Us About Your Vehicle", text: "Year, make, model and what your brakes are doing. Takes two minutes." },
  { icon: BadgeCheck, title: "Get Your Brake Quote", text: "An exact, transparent price for front, rear or both axles. No surprises." },
  { icon: Phone, title: "Call or Text to Book", text: "One quick call or text and we lock in a day and time that works for you." },
  { icon: Truck, title: "We Come to You", text: "We arrive with the parts, do the repair on site, and hand you the old ones." },
];

const NO_MORE = ["Dropping your vehicle off", "Arranging transportation", "Waiting at a repair shop", "Wasting hours in a waiting room"];

function Hero() {
  return (
    <section data-testid="hero-section" className="grain relative overflow-hidden">
      <div className="mx-auto max-w-3xl px-4 pb-16 pt-14 text-center sm:px-6 lg:pb-24 lg:pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-5 inline-flex items-center gap-2 border border-edge bg-slate2 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-amber2"
        >
          <MapPin className="h-3.5 w-3.5" /> Middlesex County, MA · Southern NH
        </motion.p>

        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.1} className="text-slate-50">My Mobile</MaskedLine>
          <MaskedLine delay={0.25} className="text-crimson">Mechanic</MaskedLine>
          <MaskedLine delay={0.4} className="text-slate-50">
            — We Come <span className="text-amber2">to You</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Professional brake repair at your home or workplace throughout Middlesex County,
          Massachusetts and Southern New Hampshire.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-8 flex justify-center"
        >
          <CTAPair />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05 }}
          className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs font-semibold uppercase tracking-wide text-slate-400"
        >
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-amber2" /> Warranty-backed</span>
          <span className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-amber2" /> Upfront pricing</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber2" /> {SITE.hoursShort}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          data-testid="hero-social-links"
          className="mt-7 flex justify-center gap-3"
        >
          {SOCIAL_ICONS.map(({ key, label, Icon }) => (
            <a
              key={key}
              href={SITE.socials[key]}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`My Mobile Mechanic on ${label}`}
              data-testid={`hero-social-${key}`}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-edge text-slate-400 transition-all duration-200 hover:-translate-y-0.5 hover:border-crimson hover:text-crimson"
            >
              <Icon className="h-5 w-5" />
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function TrustBand() {
  return (
    <section data-testid="social-proof-band" className="border-y border-edge bg-slate2">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-4 py-10 sm:px-6 lg:flex-row lg:justify-between">
        <Reveal className="text-center lg:text-left">
          <Eyebrow>Trusted by</Eyebrow>
          <p className="font-display text-4xl font-black uppercase tracking-tight text-slate-50 sm:text-5xl">
            2 Million+ <span className="text-crimson">Followers</span>
          </p>
          <p className="mt-1 text-sm text-slate-400">across Facebook, Instagram, TikTok and YouTube</p>
        </Reveal>
        <Reveal delay={0.15} className="grid grid-cols-2 gap-6 text-center sm:grid-cols-4">
          {[
            { v: "12", l: "Years in business" },
            { v: "20+", l: "Years experience" },
            { v: "2B+", l: "Video views" },
            { v: "3yr/36k", l: "Mile warranty" },
          ].map((s) => (
            <div key={s.l}>
              <p className="font-display text-3xl font-black text-amber2">{s.v}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{s.l}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section data-testid="why-choose-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      <SectionHeading
        eyebrow="Why Choose My Mobile Mechanic"
        title="An Established Pro, Not a Stranger With a Toolbox"
        sub="You're hiring a documented professional with years of real repair work — watched by nearly two million people."
      />
      <div className="mt-12 grid gap-px border border-edge bg-edge sm:grid-cols-2 lg:grid-cols-3">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={(i % 3) * 0.08} className="h-full">
            <div className="group flex h-full flex-col bg-obsidian p-7 transition-colors duration-300 hover:bg-slate2">
              <div className="flex items-center justify-between">
                <w.icon className="h-7 w-7 text-crimson transition-colors group-hover:text-amber2" />
                <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section data-testid="how-it-works-section" className="border-y border-edge bg-slate2">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading
          eyebrow="How It Works"
          title="Four Steps. Zero Waiting Rooms."
        />
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="relative">
                <p className="font-display text-7xl font-black leading-none text-elevated">{i + 1}</p>
                <div className="absolute left-0 top-0 flex h-12 w-12 translate-x-6 translate-y-4 items-center justify-center bg-crimson">
                  <s.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-slate-50">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14 border border-edge bg-obsidian p-6 sm:p-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-crimson">With us, you never have to:</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {NO_MORE.map((n) => (
              <p key={n} className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                <XCircle className="h-4 w-4 shrink-0 text-crimson" /> {n}
              </p>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MeetYourMechanic() {
  return (
    <section data-testid="meet-mechanic-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <Reveal className="order-2 lg:order-1">
          <Eyebrow>Meet Your Mechanic</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            The Same Hands From the Videos
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
            My Mobile Mechanic brings professional auto repair directly to your driveway. With
            nearly 20 years of hands-on automotive experience and a community of nearly 2 million
            followers across social media, we've built our reputation by making auto repair
            straightforward, convenient and honest.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-400">
            From brake replacement and routine maintenance to everyday repairs, we come to you —
            saving you the hassle of waiting at a repair shop. Quality work, dependable service and
            real-world experience, right in your driveway.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { v: "20+", l: "Years of experience" },
              { v: "2M+", l: "Social followers" },
              { v: "2B+", l: "Video views" },
            ].map((s) => (
              <div key={s.l} className="border border-edge bg-slate2 p-4 text-center">
                <p className="font-display text-2xl font-black text-crimson sm:text-3xl">{s.v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.l}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <div className="clip-frame relative mx-auto aspect-square max-w-md overflow-hidden border border-edge lg:max-w-none">
            <img
              src="/images/owner-van.jpg"
              alt="Your mechanic — nearly 20 years of hands-on experience, standing by the My Mobile Mechanic 978 service van"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-obsidian/90 to-transparent px-5 pb-5 pt-12">
              <p className="font-display text-xl font-bold uppercase text-slate-50">Manny — Owner & Lead Technician</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber2">My Mobile Mechanic 978</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ServiceAreaChips() {
  return (
    <section data-testid="service-areas-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      <SectionHeading
        eyebrow="Service Areas"
        title="Proudly Mobile Across the Merrimack Valley"
        sub="Plus surrounding towns — if you're nearby, just ask."
      />
      <Reveal className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {LOCATIONS.map((l) => (
          <Link
            key={l.slug}
            to={`/${l.slug}`}
            data-testid={`location-${l.city.toLowerCase()}-link`}
            className="group flex items-center justify-between border border-edge bg-slate2 px-5 py-4 transition-all duration-300 hover:-translate-y-1 hover:border-amber2/60"
          >
            <span>
              <span className="block font-display text-xl font-bold uppercase text-slate-100">{l.city}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l.state}</span>
            </span>
            <MapPin className="h-5 w-5 text-crimson transition-colors group-hover:text-amber2" />
          </Link>
        ))}
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="My Mobile Mechanic | Mobile Brake Replacement — We Come to You"
        description="Mobile brake replacement at your home or workplace across Middlesex County, MA and Southern NH. Get a brake quote, then call or text to book — we come to you."
        path="/"
      />
      <Hero />
      <TrustBand />
      <ServicesStrip />
      <Marquee />
      <WhyChoose />
      <HowItWorks />
      <MeetYourMechanic />
      <GallerySection />
      <div className="border-t border-edge bg-slate2/40">
        <ReviewsSection limit={3} />
      </div>
      <SocialSection />
      <FAQSection />
      <ServiceAreaChips />
      <CTABand sub="Book in two minutes. We bring the parts, the tools and the warranty — you keep your day." />
    </>
  );
}

export { CheckCircle2 };
